import { T } from "../lib/fc3i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, MotionConfig, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Globe, Trash2, Plus, Minus, Calculator, Handshake, Sparkles, Lock } from "lucide-react";
import { api } from "../lib/api";
import TrackOGHomepageScript from "../components/TrackOGHomepageScript";
import HeroStage from "../components/fc3/HeroStage";

/* ------------------------------------------------------------------ *
 * FemCare 3.0 homepage — light, friendly and cinematic.
 * Inspired by the clarity of leading women's-health apps, built as
 * FemCare's own design: blush / lavender / mint palette, coral + violet
 * accents, a 3D hero with real app screens, and a scroll-driven
 * feature tour. Prices come live from /api/pricing. No stats, reviews
 * or medical claims are invented here.
 * ------------------------------------------------------------------ */

const DISCLAIMER = "The information and wellness suggestions provided by FemCare are for general educational and informational purposes only and do not constitute medical advice, diagnosis, or treatment. Always consult your gynecologist or a qualified healthcare professional before following any health-related advice or making decisions about your health.";
const APP_STORE = "https://apps.apple.com/us/app/femcare-life/id6781981749";
const PLAY_STORE = "https://play.google.com/store/apps/details?id=app.femcare.life&hl=en";
const TOOLS = "https://tools.femcare.life/period-calculator/";
const FAQS = [{
  q: "Is FemCare free to use?",
  a: "Yes. The Free plan includes cycle tracking and predictions, a period and symptom logger, mood tracking, community access and basic reminders. Premium is optional and adds AI insights, wellness reports and Pregnancy Mode."
}, {
  q: "Is FemCare a replacement for my doctor?",
  a: "No. FemCare helps you track and understand patterns and gives general education. It does not diagnose or treat any condition. Please talk to your gynecologist or a qualified healthcare professional about anything that worries you."
}, {
  q: "How accurate is the period calculator?",
  a: "It gives an estimate based on the dates and cycle length you enter, assuming a regular cycle. Real cycles vary, especially with stress, illness, PCOS, after childbirth or near menopause. Tracking over several months gives a better picture."
}, {
  q: "Which languages are supported?",
  a: "The website is available in 24 languages, including English, Hindi, Bengali, Tamil, Telugu, Spanish, Arabic and more. The free period calculator is available in the same 24 languages."
}, {
  q: "What is Daughter Care?",
  a: "An add-on for Premium members that lets a parent support a daughter aged 10 to 17 with teen-safe education and period and mood tracking. It contains no fertility content."
}, {
  q: "Can I delete my account and data?",
  a: "Yes. You can request deletion of your account and its data at any time from the Delete account page linked in the footer."
}];
const HOME_JSONLD = [{
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Femcare.life",
  url: "https://femcare.life/",
  logo: "https://femcare.life/femcare-logo-v2.png",
  email: "support@femcare.life",
  sameAs: [PLAY_STORE.replace("&hl=en", ""), APP_STORE]
}, {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Femcare.life",
  operatingSystem: "Android",
  applicationCategory: "HealthApplication",
  installUrl: PLAY_STORE.replace("&hl=en", ""),
  url: PLAY_STORE.replace("&hl=en", ""),
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR"
  },
  publisher: {
    "@type": "Organization",
    name: "Femcare.life"
  }
}, {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Femcare.life",
  operatingSystem: "iOS",
  applicationCategory: "HealthApplication",
  installUrl: APP_STORE,
  url: APP_STORE,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR"
  },
  publisher: {
    "@type": "Organization",
    name: "Femcare.life"
  }
}, {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a
    }
  }))
}];
const ROTATING = ["your period", "your fertile days", "your pregnancy", "your daughter's first period"];
const TOUR = [{
  key: "cycle",
  tag: "Period & cycle",
  color: "#FF5C8A",
  bg: "#FFE9EF",
  src: "/femcare-ui-v2.jpg",
  title: "Know when your period is coming",
  body: "Log your period in a tap. FemCare learns your rhythm and shows days until your next period, the phase you're in and a colour-coded calendar.",
  to: "/features/cycle"
}, {
  key: "insights",
  tag: "Daily insights",
  color: "#6B5BEF",
  bg: "#EFEBFF",
  src: "/femcare-ui-cycle.jpg",
  title: "Tips that match your day",
  body: "Log symptoms and mood, then get short, practical tips for where you are in your cycle — with clear reminders that estimates aren't contraception.",
  to: "/features/cycle"
}, {
  key: "pregnancy",
  tag: "Pregnancy",
  color: "#E0306F",
  bg: "#FFE9EF",
  src: "/pregnancy-illustration.png",
  title: "Follow your pregnancy week by week",
  body: "Pregnancy Mode shows how far along you are, a weekly snapshot of your baby's growth and symptoms to track — so you're ready for every appointment.",
  to: "/features/pregnancy"
}, {
  key: "daughter",
  tag: "Daughter Care",
  color: "#1FB89A",
  bg: "#E3F8F2",
  src: "/femcare-ui-daughter.jpg",
  title: "Support your daughter's first periods",
  body: "Age-based puberty guidance, a first-period checklist and teen-safe tracking for girls 10–17. Parents see only what's shared.",
  to: "/features/daughter-care"
}, {
  key: "learn",
  tag: "Learn",
  color: "#F29A38",
  bg: "#FFF1DC",
  src: "/insights-articles.png",
  title: "Clear articles, no jargon",
  body: "Short reads on cycles, PCOS, nutrition, pregnancy and more — written to help you have better conversations with your doctor.",
  to: "/blog"
}];
const TRUST = [{
  i: Check,
  t: "Free plan, no card needed",
  bg: "#E3F8F2",
  c: "#13896F"
}, {
  i: Lock,
  t: "Private by design",
  bg: "#EFEBFF",
  c: "#5A49E0"
}, {
  i: Globe,
  t: "Available in 24 languages",
  bg: "#FFE9EF",
  c: "#D02868"
}, {
  i: Trash2,
  t: "Delete your data any time",
  bg: "#FFF1DC",
  c: "#B4630E"
}];

/* ---------------------- cinematic helpers ----------------------- */

const EASE = [0.22, 1, 0.36, 1];
const SANS = {
  fontFamily: "var(--fc-sans)"
};
function Reveal({
  children,
  delay = 0,
  className = "",
  y = 28
}) {
  return <motion.div className={className} initial={{
    opacity: 0,
    y
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-80px"
  }} transition={{
    duration: 0.9,
    ease: EASE,
    delay
  }}>
      {children}
    </motion.div>;
}
function Tilt({
  children,
  className = "",
  max = 8
}) {
  const ref = useRef(null);
  const onMove = e => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg) translateY(-4px)`;
    el.style.setProperty("--gx", `${(px + 0.5) * 100}%`);
    el.style.setProperty("--gy", `${(py + 0.5) * 100}%`);
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "";
  };
  return <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className={`fc-tilt transition-transform duration-300 ease-out will-change-transform ${className}`}>
      {children}
    </div>;
}
function Eyebrow({
  children,
  color = "#E0306F",
  bg = "#FFE9EF"
}) {
  return <p className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-bold mb-5" style={{
    color,
    background: bg
  }}>
      {children}
    </p>;
}
function H2({
  children,
  className = ""
}) {
  return <h2 className={`text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.06] tracking-[-0.025em] text-[#1E1A2B] ${className}`} style={{
    ...SANS,
    fontWeight: 800
  }}>
      {children}
    </h2>;
}
function StoreBadges({
  ids = ["cta-app-store", "cta-play-store"],
  className = ""
}) {
  return <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a href={APP_STORE} target="_blank" rel="noopener noreferrer" data-testid={ids[0]} aria-label={T("Download on the App Store")} className="inline-block hover:-translate-y-0.5 transition-transform">
        <img src="/app-store-badge.png" alt={T("Download on the App Store")} className="h-12 w-auto" />
      </a>
      <a href={PLAY_STORE} target="_blank" rel="noopener noreferrer" data-testid={ids[1]} aria-label={T("Get it on Google Play")} className="inline-block hover:-translate-y-0.5 transition-transform">
        <img src="/google-play-badge.png" alt={T("Get it on Google Play")} className="h-12 w-auto" />
      </a>
    </div>;
}
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setI(n => (n + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);
  return <span className="relative inline-block align-top min-h-[1.1em]" aria-live="off">
      <AnimatePresence mode="wait">
        <motion.span key={T(ROTATING[i])} className="inline-block fc-text-coral" initial={{
        opacity: 0,
        y: "0.5em",
        filter: "blur(6px)"
      }} animate={{
        opacity: 1,
        y: 0,
        filter: "blur(0px)"
      }} exit={{
        opacity: 0,
        y: "-0.5em",
        filter: "blur(6px)"
      }} transition={{
        duration: 0.55,
        ease: EASE
      }}>
          {T(ROTATING[i])}
        </motion.span>
      </AnimatePresence>
    </span>;
}

/* Flo-style feature tour: sticky phone, screen swaps as you scroll. */
function FeatureTour() {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  useEffect(() => {
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) setActive(Number(e.target.dataset.idx));
      });
    }, {
      rootMargin: "-45% 0px -45% 0px",
      threshold: 0
    });
    refs.current.forEach(n => n && io.observe(n));
    return () => io.disconnect();
  }, []);
  const cur = TOUR[active];
  return <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="hidden lg:block lg:col-span-5">
        <div className="sticky top-[110px] h-[620px] flex items-center justify-center">
          <div className="absolute inset-6 rounded-[48px] transition-colors duration-700" style={{
          background: cur.bg
        }} aria-hidden="true" />
          <div className="absolute w-[380px] h-[380px] rounded-full blur-3xl opacity-50 transition-colors duration-700" style={{
          background: cur.color
        }} aria-hidden="true" />
          <Tilt max={6} className="relative rounded-[42px]">
            <div className="fc-phone-body" style={{
            width: 270,
            animation: "none"
          }}>
              <div className="fc-phone-screen">
                <AnimatePresence mode="wait">
                  <motion.img key={cur.src} src={cur.src} alt={`FemCare app: ${cur.tag}`} initial={{
                  opacity: 0,
                  scale: 1.04
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.98
                }} transition={{
                  duration: 0.5,
                  ease: EASE
                }} className="absolute inset-0 w-full h-full" style={{
                  objectFit: /pregnancy|insights/.test(cur.src) ? "contain" : "cover",
                  background: "#FDF3F7"
                }} />
                </AnimatePresence>
                <span className="fc-phone-glare" aria-hidden="true" />
              </div>
            </div>
          </Tilt>
          <div className="absolute bottom-6 flex gap-2" aria-hidden="true">
            {TOUR.map((t, i) => <span key={t.key} className="h-2 rounded-full transition-all duration-500" style={{
            width: i === active ? 28 : 8,
            background: i === active ? cur.color : "#E5DCE2"
          }} />)}
          </div>
        </div>
      </div>
      <div className="lg:col-span-7">
        {TOUR.map((t, i) => <div key={t.key} ref={n => {
        refs.current[i] = n;
      }} data-idx={i} className="lg:min-h-[78vh] flex flex-col justify-center py-6 lg:py-0">
            <div className={`rounded-[28px] p-7 md:p-10 transition-all duration-500 ${i === active ? "bg-white shadow-[0_30px_70px_-35px_rgba(43,33,64,0.35)]" : "lg:opacity-55"}`} style={{
          borderLeft: `5px solid ${i === active ? t.color : "transparent"}`
        }}>
              <div className="lg:hidden mb-6 mx-auto w-[220px]">
                <div className="fc-phone-body" style={{
              animation: "none"
            }}>
                  <div className="fc-phone-screen"><img src={t.src} alt={`FemCare app: ${t.tag}`} loading="lazy" /></div>
                </div>
              </div>
              <span className="inline-block rounded-full px-3 py-1 text-[13px] font-bold" style={{
            background: t.bg,
            color: t.color
          }}>{T(t.tag)}</span>
              <h3 className="mt-4 text-[28px] md:text-[34px] leading-tight tracking-[-0.02em] text-[#1E1A2B]" style={{
            ...SANS,
            fontWeight: 800
          }}>{T(t.title)}</h3>
              <p className="mt-3 text-[17px] leading-relaxed text-[#5F5A70] max-w-xl">{T(t.body)}</p>
              <Link to={t.to} className="mt-6 inline-flex items-center gap-1.5 text-[16px] font-bold group" style={{
            color: t.color
          }}>{T("Learn more")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>)}
      </div>
    </div>;
}

/* --------------------------- calculator --------------------------- */

/* ---------------------------- helpers ----------------------------- */

const DAY = 86400000;
const toISO = d => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const parseISO = s => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmt = d => d.toLocaleDateString("en-IN", {
  weekday: "short",
  day: "numeric",
  month: "short"
});
const fmtShort = d => d.toLocaleDateString("en-IN", {
  day: "numeric",
  month: "short"
});
const inr = v => `₹${Number(String(v).replace(/,/g, "")).toLocaleString("en-IN")}`;
function Stepper({
  id,
  label,
  value,
  setter,
  min,
  max,
  unit
}) {
  const step = v => setter(Math.min(max, Math.max(min, v)));
  return <div>
      <label htmlFor={id} className="block text-[14px] font-semibold text-[#1E1A2B] mb-2">{T(label)}</label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => step(value - 1)} aria-label={`Decrease ${label}`} className="w-12 h-12 rounded-full border border-[#F1E6EA] bg-white text-[#2B2140] hover:border-[#2B2140] flex items-center justify-center">
          <Minus className="w-4 h-4" />
        </button>
        <input id={id} type="number" min={min} max={max} value={value} onChange={e => step(parseInt(e.target.value || min, 10))} className="w-20 h-12 text-center rounded-xl border border-[#F1E6EA] bg-white text-[18px] font-semibold text-[#1E1A2B]" />
        <button type="button" onClick={() => step(value + 1)} aria-label={`Increase ${label}`} className="w-12 h-12 rounded-full border border-[#F1E6EA] bg-white text-[#2B2140] hover:border-[#2B2140] flex items-center justify-center">
          <Plus className="w-4 h-4" />
        </button>
        <span className="text-[14px] text-[#5F5A70]">{T(unit)}</span>
      </div>
    </div>;
}
function PeriodCalculator() {
  const defaultStart = useMemo(() => toISO(new Date(Date.now() - 10 * DAY)), []);
  const [start, setStart] = useState(defaultStart);
  const [cycle, setCycle] = useState(28);
  const [length, setLength] = useState(5);
  const [shown, setShown] = useState(false);
  const result = useMemo(() => {
    if (!start) return null;
    const s = parseISO(start);
    const periods = [1, 2, 3].map(i => {
      const a = new Date(s.getTime() + cycle * i * DAY);
      const b = new Date(a.getTime() + (length - 1) * DAY);
      return {
        a,
        b
      };
    });
    const next = periods[0].a;
    const ovulation = new Date(next.getTime() - 14 * DAY);
    const fertileFrom = new Date(ovulation.getTime() - 5 * DAY);
    const fertileTo = new Date(ovulation.getTime() + 1 * DAY);
    return {
      periods,
      ovulation,
      fertileFrom,
      fertileTo
    };
  }, [start, cycle, length]);
  return <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      <form className="lg:col-span-5 rounded-[28px] bg-[#FFF8F9] border border-[#F1E6EA] p-6 md:p-8 flex flex-col gap-6" onSubmit={e => {
      e.preventDefault();
      setShown(true);
    }} data-testid="home-calculator-form">
        <div>
          <label htmlFor="calc-start" className="block text-[14px] font-semibold text-[#1E1A2B] mb-2">{T("First day of your last period")}</label>
          <input id="calc-start" type="date" value={start} max={toISO(new Date())} onChange={e => setStart(e.target.value)} required className="w-full h-12 rounded-xl border border-[#F1E6EA] bg-white px-4 text-[16px] text-[#1E1A2B]" />
        </div>
        <Stepper id="calc-cycle" label="Average cycle length" value={cycle} setter={setCycle} min={21} max={45} unit="days" />
        <Stepper id="calc-length" label="Period length" value={length} setter={setLength} min={2} max={10} unit="days" />
        <button type="submit" className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6" data-testid="home-calculator-submit">{T("Calculate")} <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      <div className="lg:col-span-7" aria-live="polite">
        {shown && result ? <div className="rounded-[28px] bg-white border border-[#F1E6EA] p-6 md:p-8" data-testid="home-calculator-result">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#E0306F]">{T("Your next period (estimate)")}</p>
            <p className="mt-2 text-[34px] md:text-[42px] leading-tight text-[#2B2140]" style={{
          fontFamily: "var(--fc-sans)",
          fontWeight: 800
        }}>
              {fmt(result.periods[0].a)} – {fmtShort(result.periods[0].b)}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl bg-[#EFEBFF] p-5">
                <p className="text-[13px] font-semibold text-[#6B5BEF]">{T("Estimated fertile window")}</p>
                <p className="mt-1 text-[18px] font-semibold text-[#1E1A2B]">{fmtShort(result.fertileFrom)} – {fmtShort(result.fertileTo)}</p>
              </div>
              <div className="rounded-2xl bg-[#FFE9EF] p-5">
                <p className="text-[13px] font-semibold text-[#E0306F]">{T("Estimated ovulation")}</p>
                <p className="mt-1 text-[18px] font-semibold text-[#1E1A2B]">{T("around {{date}}", { date: fmtShort(result.ovulation) })}</p>
              </div>
            </div>
            <p className="mt-6 text-[14px] font-semibold text-[#1E1A2B]">{T("The next three periods")}</p>
            <ul className="mt-2 divide-y divide-[#F1E6EA]">
              {result.periods.map((p, i) => <li key={i} className="py-3 flex justify-between text-[15px]">
                  <span className="text-[#5F5A70]">{T("Period")} {i + 1}</span>
                  <span className="font-semibold text-[#1E1A2B]">{fmtShort(p.a)} – {fmtShort(p.b)}</span>
                </li>)}
            </ul>
            <p className="mt-5 text-[13px] leading-relaxed text-[#5F5A70]">{T("Estimates assume a regular cycle and ovulation about 14 days before your next period. They are not a method of contraception. If your periods are very irregular, very painful or unusually heavy, please speak to a gynecologist.")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/app/signup" className="btn-primary inline-flex items-center gap-2 rounded-full px-6">{T("Track it in FemCare")} <ArrowRight className="w-4 h-4" /></a>
              <a href={TOOLS} className="inline-flex items-center min-h-[48px] rounded-full px-6 font-semibold text-[#2B2140] border border-[#2B2140] hover:bg-[#2B2140] hover:text-white transition-colors">{T("Use it in 24 languages")}</a>
            </div>
          </div> : <div className="rounded-[28px] border border-dashed border-[#F3C9D6] p-8 md:p-10 h-full flex flex-col justify-center">
            <Calculator className="w-8 h-8 text-[#E0306F]" aria-hidden="true" />
            <p className="mt-4 text-[26px] leading-snug text-[#2B2140]" style={{
          fontFamily: "var(--fc-sans)",
          fontWeight: 800
        }}>{T("Enter your dates to see your next period, fertile window and the next three cycles.")}</p>
            <p className="mt-3 text-[15px] text-[#5F5A70]">{T("Nothing you enter here is saved or sent anywhere — it's calculated in your browser.")}</p>
          </div>}
      </div>
    </div>;
}

/* ------------------------------ page ------------------------------ */

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  useEffect(() => {
    api.get("/posts?limit=3").then(r => setPosts(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(console.error);
    api.get("/pricing").then(r => setPlans(Array.isArray(r.data) ? r.data : [])).catch(console.error);
  }, []);
  const free = plans.find(p => p.tier === "free");
  const monthly = plans.find(p => p.name === "Premium");
  const yearly = plans.find(p => p.name === "Premium Annual");
  const addon = plans.find(p => p.name === "Daughter Care");
  return <MotionConfig reducedMotion="user">
    <div data-testid="home-page" className="bg-[#FFF8F9] text-[#1E1A2B] overflow-x-clip">
      <script type="application/ld+json" data-testid="home-jsonld" dangerouslySetInnerHTML={{
        __html: JSON.stringify(HOME_JSONLD)
      }} />
      {/* Homepage-only third-party visitor pixel — never mounted anywhere else. */}
      <TrackOGHomepageScript />

      {/* HERO */}
      <section className="relative overflow-hidden fc-hero-light" data-testid="home-hero">
        <div className="relative max-w-[1240px] mx-auto px-5 lg:px-8 pt-10 pb-16 lg:pt-16 lg:pb-24 grid lg:grid-cols-12 gap-4 lg:gap-8 items-center">
          <div className="lg:col-span-6 relative z-10">
            <motion.p initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              ease: EASE
            }} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#F3D5DF] px-4 py-2 text-[13px] font-bold text-[#D02868] shadow-[0_8px_24px_-14px_rgba(224,48,111,0.5)]">
              <Sparkles className="w-4 h-4" aria-hidden="true" /> {T("Women's health for every stage of life")}</motion.p>
            <h1 className="mt-6 text-[44px] sm:text-[56px] lg:text-[66px] leading-[1.04] tracking-[-0.035em] text-[#1E1A2B]" style={{
              ...SANS,
              fontWeight: 800
            }}>
              <motion.span className="block" initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.9,
                ease: EASE,
                delay: 0.05
              }}>{T("Understand")}</motion.span>
              <motion.span className="block" initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.9,
                ease: EASE,
                delay: 0.15
              }}>
                <RotatingWord />
              </motion.span>
            </h1>
            <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.9,
              ease: EASE,
              delay: 0.3
            }} className="mt-6 text-[18px] lg:text-[20px] leading-relaxed text-[#5F5A70] max-w-xl">{T("FemCare tracks your cycle, predicts your next period and fertile window, follows your pregnancy and helps you support your daughter — privately, in one friendly app.")}</motion.p>
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.9,
              ease: EASE,
              delay: 0.42
            }} className="mt-9 flex flex-wrap gap-3">
              <a href="/app/signup" className="fc-btn-coral inline-flex items-center gap-2 min-h-[56px] rounded-full px-8 text-[17px] font-extrabold" data-testid="hero-cta-signup">{T("Get started free")} <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#calculator" className="inline-flex items-center gap-2 min-h-[56px] rounded-full px-7 text-[16px] font-bold text-[#2B2140] bg-white border border-[#E9DCE3] hover:border-[#2B2140] transition-colors" data-testid="hero-cta-calculator">
                <Calculator className="w-5 h-5 text-[#6B5BEF]" aria-hidden="true" /> {T("Free period calculator")}</a>
            </motion.div>
            <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 1,
              delay: 0.6
            }}>
              <StoreBadges className="mt-7" />
            </motion.div>
          </div>
          <motion.div className="lg:col-span-6 relative h-[520px] sm:h-[580px] lg:h-[660px]" initial={{
            opacity: 0,
            scale: 0.94
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.2,
            ease: EASE,
            delay: 0.15
          }}>
            <HeroStage />
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative z-10 -mt-6 px-5 lg:px-8" aria-label={T("Why FemCare")}>
        <ul className="max-w-[1240px] mx-auto rounded-[24px] bg-white border border-[#F3E3EA] shadow-[0_30px_60px_-34px_rgba(43,33,64,0.35)] px-5 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-[15px]" data-testid="marketing-tagline">
          {TRUST.map(({
            i: I,
            t,
            bg,
            c
          }) => <li key={t} className="flex items-center gap-3 font-semibold text-[#2B2140]">
              <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{
              background: bg,
              color: c
            }}><I className="w-5 h-5" aria-hidden="true" /></span>
              {T(t)}
            </li>)}
        </ul>
      </section>

      {/* FEATURE TOUR */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 pt-24 lg:pt-32" data-testid="home-tour">
        <Reveal className="text-center max-w-3xl mx-auto mb-8 lg:mb-4">
          <Eyebrow color="#5A49E0" bg="#EFEBFF">{T("One app, every stage")}</Eyebrow>
          <H2>{T("What can you do with FemCare?")}</H2>
          <p className="mt-4 text-[18px] text-[#5F5A70]">{T("Real screens from the FemCare app. Scroll to explore.")}</p>
        </Reveal>
        <FeatureTour />
      </section>

      {/* FITNESS TEASER */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 pt-20" data-testid="home-fitness">
        <Link to="/fitness" className="group block rounded-[36px] overflow-hidden fc-ai-card p-7 md:p-12 relative">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <Eyebrow color="#D02868" bg="#FFFFFF">{T("New · Included in Premium")}</Eyebrow>
              <H2>{T("FemCare Fitness, made for women")}</H2>
              <p className="mt-4 text-[18px] text-[#4E4862] max-w-xl">{T("Yoga, dance cardio (Zumba-style), strength, Pilates and walking plans with a timer and voice guidance — suggested for your cycle phase. Plus gyms and studios near you.")}</p>
              <span className="fc-btn-coral mt-8 inline-flex items-center gap-2 min-h-[54px] rounded-full px-7 font-extrabold">{T("Explore Fitness")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3" aria-hidden="true">
              {[["Yoga", "#EFEBFF", "#5A49E0"], ["Dance & Zumba-style", "#FFE9EF", "#D02868"], ["Strength", "#FFF1DC", "#B4630E"], ["Gyms near you", "#E3F8F2", "#13896F"]].map(([t, bg, c], i) => <div key={t} className="rounded-[22px] p-5 bg-white/90 shadow-[0_20px_40px_-28px_rgba(43,33,64,0.5)] transition-transform group-hover:-translate-y-1" style={{
                transitionDelay: `${i * 40}ms`
              }}>
                  <span className="block w-10 h-10 rounded-xl" style={{
                  background: bg,
                  boxShadow: `inset 0 0 0 3px ${c}22`
                }} />
                  <p className="mt-3 text-[16px] font-extrabold" style={{
                  color: c
                }}>{t}</p>
                </div>)}
            </div>
          </div>
        </Link>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="fc-section-lav scroll-mt-24 mt-16" data-testid="home-calculator">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <Reveal className="max-w-2xl mb-10">
            <Eyebrow color="#13896F" bg="#E3F8F2">{T("Free tool · no sign-up")}</Eyebrow>
            <H2>{T("Period & ovulation calculator")}</H2>
            <p className="mt-4 text-[18px] text-[#5F5A70]">{T("A quick estimate of your next period and fertile days, calculated in your browser.")}</p>
          </Reveal>
          <PeriodCalculator />
        </div>
      </section>

      {/* AI COMPANION */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid="home-ai">
        <div className="fc-ai-card relative overflow-hidden rounded-[36px] p-7 md:p-12 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative">
            <Eyebrow color="#5A49E0" bg="#FFFFFF">{T("AI Wellness Companion · Premium")}</Eyebrow>
            <H2>{T("Ask anything. Get a calm, clear answer.")}</H2>
            <p className="mt-5 text-[18px] leading-relaxed text-[#4E4862] max-w-xl">{T("The AI companion explains what you're noticing in plain language, using the cycle data you choose to log — and tells you when it's time to see a doctor.")}</p>
            <ul className="mt-7 space-y-3 text-[16px] text-[#2B2140] font-semibold">
              {["Available in English and हिंदी", "Uses your own logs to personalise answers", "General education — never a diagnosis"].map(b => <li key={b} className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-[#6B5BEF]" aria-hidden="true" /></span>{b}</li>)}
            </ul>
            <Link to="/features/ai-coach" className="fc-btn-violet mt-9 inline-flex items-center gap-2 min-h-[54px] rounded-full px-7 font-extrabold" data-testid="premium-cta">{T("Meet the companion")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 relative">
            <Tilt max={5} className="rounded-[28px]">
              <div className="rounded-[28px] bg-white/85 backdrop-blur p-6 md:p-8 shadow-[0_40px_80px_-40px_rgba(90,73,224,0.55)] border border-white" aria-label={T("Illustrative conversation")}>
                <div className="flex justify-end">
                  <p className="max-w-[85%] rounded-[22px] rounded-br-md fc-bubble-user px-5 py-3 text-[15px] leading-relaxed text-white">{T("My cycle was 35 days this month instead of my usual 29. Should I be worried?")}</p>
                </div>
                <div className="mt-4 flex gap-3">
                  <img src="/femcare-logo-v2.png" alt="" className="w-9 h-9 rounded-full shrink-0 bg-white" />
                  <div className="max-w-[88%] rounded-[22px] rounded-tl-md bg-[#F6F3FF] text-[#1E1A2B] px-5 py-4 text-[15px] leading-relaxed">{T("A one-off change of a few days is common — stress, travel, illness or sleep changes can shift ovulation. Keep logging for the next couple of cycles.")} <span className="block mt-2">{T("Please see a gynecologist if cycles stay longer than 35 days, you miss periods, or there's any chance you could be pregnant.")}</span>
                  </div>
                </div>
                <p className="mt-5 text-[12px] text-[#7A7488]">{T("Illustrative example. The companion gives general information, not medical advice.")}</p>
              </div>
            </Tilt>
          </div>
        </div>
      </section>

      {/* PLANS — live prices */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 pb-20 lg:pb-28" data-testid="home-plans">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>{T("Plans")}</Eyebrow>
          <H2>{T("Start free. Upgrade when you want more.")}</H2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5 max-w-[1040px] mx-auto">
          <div className="rounded-[30px] bg-white border border-[#F1E6EA] p-7 md:p-9 flex flex-col">
            <p className="text-[15px] font-extrabold text-[#5F5A70]">{T("Free")}</p>
            <p className="mt-3 text-[52px] leading-none text-[#1E1A2B]" style={{
              ...SANS,
              fontWeight: 800
            }}>
              {free ? inr(free.price) : "₹0"}<span className="text-[16px] text-[#5F5A70] ml-2 font-semibold">{T("forever")}</span>
            </p>
            <ul className="mt-7 space-y-3 text-[16px]">
              {(free?.features || ["Cycle tracking & predictions", "Period & symptom logger", "Mood tracking"]).map(f => <li key={f} className="flex gap-3"><Check className="w-5 h-5 text-[#1FB89A] shrink-0 mt-0.5" aria-hidden="true" />{f}</li>)}
            </ul>
            <a href="/app/signup" className="mt-auto pt-8">
              <span className="inline-flex items-center justify-center w-full min-h-[54px] rounded-full font-extrabold text-[#2B2140] border-2 border-[#2B2140] hover:bg-[#2B2140] hover:text-white transition-colors">{T("Get started free")}</span>
            </a>
          </div>
          <div className="fc-premium-light rounded-[30px] text-white p-7 md:p-9 flex flex-col relative overflow-hidden">
            <span className="absolute top-6 right-6 rounded-full bg-white/20 px-3 py-1 text-[12px] font-bold">{T("Most complete")}</span>
            <p className="text-[15px] font-extrabold text-white/85">{T("Premium")}</p>
            {monthly || yearly ? <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
                {monthly && <p className="text-[52px] leading-none" style={{
                ...SANS,
                fontWeight: 800
              }} data-testid="home-price-monthly">
                    {inr(monthly.price)}<span className="text-[16px] text-white/80 ml-1 font-semibold">{T("/month")}</span>
                  </p>}
                {yearly && <p className="text-[19px] text-white/90 pb-1 font-semibold" data-testid="home-price-yearly">{T("or {{price}}/year", { price: inr(yearly.price) })}</p>}
              </div> : <p className="mt-3 text-[20px] text-white/85">{T("See current prices on the Premium page")}</p>}
            <ul className="mt-7 space-y-3 text-[16px] text-white/95">
              {[...(monthly?.features || []).filter(f => f !== "Everything in Free").slice(0, 5), "FemCare Fitness: yoga, dance & strength library"].map(f => <li key={f} className="flex gap-3"><Check className="w-5 h-5 text-white shrink-0 mt-0.5" aria-hidden="true" />{f}</li>)}
            </ul>
            {addon && <p className="mt-6 text-[14px] text-white/80">{T("Daughter Care add-on from {{price}}/month (requires Premium).", { price: inr(addon.price) })}</p>}
            <Link to="/pricing" className="mt-auto pt-8" data-testid="home-plans-cta">
              <span className="inline-flex items-center justify-center gap-2 w-full min-h-[54px] rounded-full font-extrabold bg-white text-[#2B2140] hover:bg-[#FFF1F5] transition-colors">{T("Compare plans")} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
        <p className="mt-4 text-[13px] text-[#7A7488] text-center">{T("Prices in Indian rupees for web purchases. In-app prices on the App Store and Google Play may differ by country.")}</p>
      </section>

      {/* LEARN */}
      {posts.length > 0 && <section className="bg-white" data-testid="home-learn">
          <div className="max-w-[1240px] mx-auto px-5 lg:px-8 py-20 lg:py-28">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
              <Reveal className="max-w-2xl">
                <Eyebrow color="#B4630E" bg="#FFF1DC">{T("Health library")}</Eyebrow>
                <H2>{T("Clear guides, plain language")}</H2>
              </Reveal>
              <Link to="/blog" className="inline-flex items-center gap-1 text-[16px] font-extrabold text-[#E0306F] group" data-testid="journal-view-all">{T("All articles")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.map((p, i) => <Reveal key={p.id || p.slug} delay={i * 0.08}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full rounded-[26px] bg-[#FFF8F9] overflow-hidden border border-[#F3E3EA] hover:shadow-[0_30px_60px_-30px_rgba(43,33,64,0.35)] hover:-translate-y-1 transition-all" data-testid={`home-post-${p.slug}`}>
                    {p.cover_image && <div className="aspect-[16/10] overflow-hidden bg-[#FFE9EF]">
                        <img src={p.cover_image} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      </div>}
                    <div className="p-6">
                      <p className="text-[13px] font-bold text-[#E0306F]">{p.category}{p.read_time ? ` · ${p.read_time} min read` : ""}</p>
                      <h3 className="mt-2 text-[21px] leading-snug text-[#1E1A2B] group-hover:text-[#E0306F] transition-colors" style={{
                    ...SANS,
                    fontWeight: 800
                  }}>{T(p.title)}</h3>
                      {p.excerpt && <p className="mt-2 text-[15px] text-[#5F5A70] line-clamp-2">{p.excerpt}</p>}
                    </div>
                  </Link>
                </Reveal>)}
            </div>
          </div>
        </section>}

      {/* FAQ */}
      <section className="max-w-[920px] mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid="home-faq">
        <div className="text-center">
          <Eyebrow color="#5A49E0" bg="#EFEBFF">{T("Questions")}</Eyebrow>
          <H2>{T("Good to know")}</H2>
        </div>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return <div key={f.q} className={`rounded-[22px] border transition-colors ${open ? "bg-white border-[#EADFF7] shadow-[0_20px_40px_-30px_rgba(90,73,224,0.5)]" : "bg-white/60 border-[#F1E6EA]"}`}>
                <h3>
                  <button type="button" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open} aria-controls={`faq-a-${i}`} className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left text-[17px] font-bold text-[#1E1A2B]" data-testid={`home-faq-${i}`}>
                    {T(f.q)}
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? "bg-[#6B5BEF] text-white" : "bg-[#F6F3FF] text-[#6B5BEF]"}`}>
                      {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                </h3>
                {open && <p id={`faq-a-${i}`} className="px-6 pb-6 pr-16 text-[16px] leading-relaxed text-[#5F5A70]">{T(f.a)}</p>}
              </div>;
          })}
        </div>
      </section>

      {/* PARTNER */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 pb-10" data-testid="home-affiliate-section">
        <div className="rounded-[28px] bg-[#FFF1DC] p-7 md:p-10 flex flex-col md:flex-row md:items-center gap-6" data-testid="home-affiliate-card">
          <span className="w-14 h-14 rounded-2xl bg-white text-[#B4630E] flex items-center justify-center shrink-0"><Handshake className="w-6 h-6" aria-hidden="true" /></span>
          <div className="flex-1">
            <h3 className="text-[22px] text-[#1E1A2B]" style={{
              ...SANS,
              fontWeight: 800
            }}>{T("Creators, clinicians and community leaders")}</h3>
            <p className="mt-1 text-[16px] text-[#5F5A70]">{T("Join the FemCare partner program and earn commission on Premium sign-ups you refer.")}</p>
          </div>
          <Link to="/affiliate" className="inline-flex items-center gap-2 min-h-[52px] rounded-full px-6 font-extrabold bg-[#1E1A2B] text-white hover:bg-black transition-colors shrink-0" data-testid="home-affiliate-cta">{T("Become a partner")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1240px] mx-auto px-5 lg:px-8 py-14">
        <div className="fc-final-cta rounded-[36px] text-white px-7 py-14 md:p-16 grid lg:grid-cols-12 gap-8 items-center relative overflow-hidden" data-testid="final-cta">
          <div className="lg:col-span-7 relative">
            <ShieldCheck className="w-9 h-9 text-white/90" aria-hidden="true" />
            <h2 className="mt-5 text-[36px] md:text-[54px] leading-[1.05] tracking-[-0.03em]" style={{
              ...SANS,
              fontWeight: 800
            }}>{T("Your body, understood. Privately.")}</h2>
            <p className="mt-4 text-[18px] text-white/90 max-w-xl">{T("Start free on the web or in the app. Upgrade only if you want more.")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="/app/signup" className="inline-flex items-center gap-2 min-h-[56px] rounded-full px-8 font-extrabold bg-white text-[#2B2140] hover:bg-[#FFF1F5] transition-colors" data-testid="bottom-cta">{T("Get started free")} <ArrowRight className="w-4 h-4" />
              </a>
              <a href="/app/login" className="inline-flex items-center min-h-[56px] rounded-full px-7 font-bold text-white border-2 border-white/60 hover:border-white transition-colors" data-testid="bottom-signin">{T("Log in")}</a>
            </div>
            <StoreBadges ids={["final-app-store", "final-play-store"]} className="mt-7" />
          </div>
          <div className="lg:col-span-5 relative hidden lg:flex justify-center" aria-hidden="true">
            <div className="fc-phone-body rotate-[8deg] translate-y-10" style={{
              width: 240,
              animation: "none"
            }}>
              <div className="fc-phone-screen"><img src="/femcare-ui-cycle.jpg" alt="" loading="lazy" /></div>
            </div>
          </div>
        </div>
        <p className="mt-8 text-[13px] leading-relaxed text-[#7A7488] max-w-4xl mx-auto text-center" data-testid="home-disclaimer">{T(DISCLAIMER)}</p>
      </section>
    </div>
    </MotionConfig>;
}